/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import {useTranslation} from 'react-i18next'
import {useLocation, useNavigate} from 'react-router-dom'
import type {Offer} from 'types/entities/Offer'
import type {OfferCalculation} from 'types/entities/OfferCalculation'
import UiButton, {UiButtonSize, UiButtonStyle} from 'components/Ui/UiButton'
import {useAppDispatch} from 'helpers/store'
import {closeSecondaryModal} from 'helpers/actions'
import {ModalSize} from 'types/enums/ModalSize'
import CreateOfferCalculationModal from 'components/Offers/CreateOfferCalculationModal'
import BookingSellerDuplicateOfferModal from 'pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerDuplicateOfferModal'
import {OffersAction} from 'store/offers/OffersAction'
import {toast} from 'utils/toastUtils'
import ReservationCheckoutPlannerModal from 'components/ReservationCheckoutPlanner/ReservationCheckoutPlannerModal'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import {Tooltip} from 'react-tooltip'
import {useSecondaryConfirmModal} from 'utils/hooks/useSecondaryConfirmModal'
import {useOpenSecondaryModal} from 'utils/hooks/useOpenSecondaryModal'
import {useOptionalOfferDetailContext} from 'pages/Front/Offers/components/OfferDetailContext'
import {resolveOfferReturnTo} from 'utils/offers/offersUtils'
import BookingSellerPaymentTypeModal from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerPaymentTypeModal";
import {exportOfferConfirmationPdf} from 'utils/offers/offerPdfExport'
import { capturePosthogEvent } from 'utils/posthogUtils'

interface OfferDetailActionsProps {
    selectedOffer?: Offer
    targetCalculation?: OfferCalculation
    isExportingPdf?: boolean
    setIsExportingPdf?: (val: boolean) => void
    onClose?: () => void
}

const OfferDetailActions: React.FC<OfferDetailActionsProps> = ({
    selectedOffer: selectedOfferProp,
    targetCalculation: targetCalculationProp,
    isExportingPdf: isExportingPdfProp,
    setIsExportingPdf: setIsExportingPdfProp,
    onClose: onCloseProp,
}) => {
    const { t } = useTranslation()
    const offerDetailContext = useOptionalOfferDetailContext()
    const selectedOffer = selectedOfferProp ?? offerDetailContext?.selectedOffer
    const targetCalculation = targetCalculationProp ?? offerDetailContext?.targetCalculation
    const isExportingPdf = isExportingPdfProp ?? offerDetailContext?.isExportingPdf ?? false
    const setIsExportingPdf = setIsExportingPdfProp ?? offerDetailContext?.setIsExportingPdf ?? (() => {})
    const onClose = onCloseProp ?? offerDetailContext?.onClose
    const dispatch = useAppDispatch()
    const openConfirmModal = useSecondaryConfirmModal()
    const { openModal, closeModal } = useOpenSecondaryModal()
    const navigate = useNavigate()
    const location = useLocation()

    const returnTo = resolveOfferReturnTo(location.state as { returnTo?: string } | null)

    if (!selectedOffer) {
        return null
    }

    const onDuplicateHandler = () => {
        capturePosthogEvent('offer_duplicated', { offer_id: selectedOffer.id })
        openModal(
            <BookingSellerDuplicateOfferModal
                offer_id={selectedOffer.id}
                title={t('Duplicate offer')}
                closeModal={closeModal}
            />,
            {
                modalSize: ModalSize.Small,
                closeModal,
            },
            t('Duplicate Offer')
        )
    }

	const onPaymentTypeHandler = () => {
        openModal(
            <BookingSellerPaymentTypeModal
                offer_id={selectedOffer.id}
                title={t('Payment Type')}
                closeModal={closeModal}
            />,
            {
                modalSize: ModalSize.Small,
                closeModal,
            },
            t('Payment Type')
        )
    }

    const openCheckoutPlannerModal = () => {
        capturePosthogEvent('checkout_planner_opened', { offer_id: selectedOffer.id })
        openModal(
            <ReservationCheckoutPlannerModal
                offerId={selectedOffer.id}
                initialPlans={selectedOffer.extra?.reservation_checkout_plan ?? []}
                initialInvoiceNote={selectedOffer.extra?.invoice_note}
                initialOfferNote={selectedOffer.extra?.offer_note}
            />,
            {
                modalSize: ModalSize.Medium,
                closeModal,
            },
            t('Checkout Planner')
        )
    }

    const onCheckoutPlannerHandler = () => {
        const reservationId = selectedOffer.reservation?.id
        // Ak existuje rezervácia, zobraz AlertConfirmation
        if (reservationId) {
            openModal(
                <UiAlertConfirmationModal
                    title={t('Duplicitná rezervácia')}
                    message={
                        <div className="flex flex-col gap-2">
                            <span>{t('K tejto ponuke už existuje rezervácia. Pokračovaním bude pôvodná rezervácia vymazaná a vytvorí sa nová.')}</span>
                        </div>
                    }
                    confirmLabel={t('Vymazať a pokračovať')}
                    cancelLabel={t('Zrušiť')}
                    onConfirm={async () => {
                        try {
                            // await dispatch(BookingLoginAction.fetchDeleteReservationOffer(reservationId))
                            // toast.success(t('Pôvodná rezervácia bola vymazaná.'))
                            closeModal()
                            openCheckoutPlannerModal()
                        } catch (e) {
                            toast.error(t('Vymazanie rezervácie zlyhalo.'))
                        }
                    }}
                    onCancel={closeModal}
                    confirmStyle={UiButtonStyle.Primary}
                />,
                {
                    modalSize: ModalSize.Small,
                    closeModal,
                },
                t('Duplicitná rezervácia')
            )
            return
        }

        openCheckoutPlannerModal()
    }

    const onDeleteHandler = () => {
        openConfirmModal({
            title: t('Delete Offer'),
            message: t('Are you sure you want to delete this offer?'),
            onConfirm: async () => {
                try {
                    await dispatch(OffersAction.fetchDeleteOffer(selectedOffer.id))
                    capturePosthogEvent('offer_deleted', { offer_id: selectedOffer.id })
                    toast.success(t('Offer deleted'))
                    if (onClose) {
                        onClose()
                    } else {
                        navigate(returnTo)
                    }
                    dispatch(closeSecondaryModal())
                } catch (e) {
                    console.error(e)
                    toast.error(t('Failed to delete offer'))
                    dispatch(closeSecondaryModal())
                }
            },
            modalSize: ModalSize.Small,
            modalTitle: t('Confirm'),
        })
    }

    const onExportConfirmationPdfHandler = async () => {
        if (!targetCalculation?.id) {
            toast.error(t('No calculation available to create booking'))
            return
        }

        setIsExportingPdf(true)
        try {
            const exported = await exportOfferConfirmationPdf({
                dispatch,
                exportCalculationId: targetCalculation.id,
                offerNumber: selectedOffer.number,
                calculationName: targetCalculation.name,
                fallbackCalculationIndex: 1,
            })

            if (exported) {
                capturePosthogEvent('offer_pdf_exported', {
                    offer_id: selectedOffer.id,
                    calculation_id: targetCalculation.id,
                })
            } else {
                toast.error(t('Export failed'))
            }
        } catch (error) {
            console.error(error)
            toast.error(t('Export failed'))
        } finally {
            setIsExportingPdf(false)
        }
    }

    return (
        <div className="OfferDetailActions UiCard p-3 flex flex-col gap-2">
            <div className="flex flex-col gap-2">
                <UiButton
                    title={t('New Calculation')}
                    size={UiButtonSize.Small}
                    style={UiButtonStyle.Primary}
                    onClick={() => {
                        openModal(
                            <CreateOfferCalculationModal
                                offer_id={selectedOffer.id}
                                is_secondary={true}
                                onCreated={() => {
                                    capturePosthogEvent('offer_calculation_created', { offer_id: selectedOffer.id })
                                    dispatch(OffersAction.fetchOfferById(selectedOffer.id))
                                }}
                            />,
                            {
                                modalSize: ModalSize.RoundedFullscreen,
                                closeModal,
                            },
                            t('New Calculation')
                        )
                    }}
                    className="h-9 text-xs"
                />
                <UiButton
                    title={t('Duplicate Offer')}
                    size={UiButtonSize.Small}
                    style={UiButtonStyle.Primary}
                    onClick={onDuplicateHandler}
                    className="h-9 text-xs"
                />
				<UiButton
                    title={t('Export Confirmation PDF')}
                    size={UiButtonSize.Small}
                    style={UiButtonStyle.Primary}
                    onClick={onExportConfirmationPdfHandler}
                    disabled={isExportingPdf || !targetCalculation?.id}
                    className="h-9 text-xs"
                />
				<UiButton
                    title={t('Payment Type')}
                    size={UiButtonSize.Small}
					style={UiButtonStyle.Primary}
                    onClick={onPaymentTypeHandler}
                    className="h-9 text-xs"
                />
                <UiButton
                    title={t('Checkout Planner')}
                    size={UiButtonSize.Small}
                    style={UiButtonStyle.Primary}
                    onClick={onCheckoutPlannerHandler}
                    className="h-9 text-xs"
                />
                <UiButton
                    title={t('Delete Offer')}
                    size={UiButtonSize.Small}
                    style={UiButtonStyle.Red}
                    onClick={onDeleteHandler}
                    className="h-9 text-xs"
                />
            </div>
            <Tooltip id="calculation-item-action-tooltip" style={{ zIndex: 99999 }} />

        </div>
    )
}

export default OfferDetailActions
